import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pencil, X, Image as ImageIcon, RotateCcw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Ctx = createContext(null);
export const useContent = () => useContext(Ctx);

const hashText = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36) + s.length.toString(36);
};
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "SVG", "IMG", "VIDEO", "CANVAS"]);
const inEditUi = (el) => el.closest?.("[data-edit-ui]") ||
  el.closest?.("[data-testid='content-text-editor']") || el.closest?.("[data-testid='content-image-editor']");
const directText = (el) =>
  Array.from(el.childNodes || []).find((c) => c.nodeType === 3 && c.nodeValue.trim().length > 1);

export function Editable({ ck, fallback, as: Tag = "span", className }) {
  const c = useContent();
  const text = c?.content?.[ck] ?? fallback;
  if (!c?.editMode) return <Tag className={className}>{text}</Tag>;
  return (
    <Tag data-testid={`editable-${ck}`} title="Click to edit (admin only)"
      className={`${className || ""} cursor-pointer outline-dashed outline-1 outline-offset-2 outline-cyan-400/70 hover:outline-cyan-300 hover:bg-cyan-400/10 transition-colors`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); c.openText(ck, fallback); }}>
      {text}
    </Tag>
  );
}

function TextEditor({ ed, current, onClose, save }) {
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  const doSave = async (v) => {
    setBusy(true);
    if (await save(ed.ck, v)) onClose();
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-cyan w-full max-w-xl animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="content-text-editor">
        <div className="flex items-center justify-between p-4 border-b border-pb-border">
          <span className="font-display font-black uppercase text-lg flex items-center gap-2"><Pencil className="w-4 h-4 text-pb-cyan" /> Edit Text</span>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="text-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <textarea data-testid="content-text-input" value={val} onChange={(e) => setVal(e.target.value)} rows={4} autoFocus
            className="w-full bg-pb-elevated border border-pb-border p-3 text-white text-sm focus:outline-none focus:border-pb-cyan resize-y" />
          <div className="flex gap-2 justify-between">
            <button data-testid="content-text-reset" disabled={busy} onClick={() => doSave(null)}
              className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[11px] font-bold uppercase tracking-widest px-3 py-2 hover:border-pb-red hover:text-pb-red transition-colors">
              <RotateCcw className="w-3 h-3" /> Reset to default
            </button>
            <button data-testid="content-text-save" disabled={busy || !val.trim()} onClick={() => doSave(val.trim())}
              className="bg-pb-cyan text-black font-bold uppercase tracking-widest text-[11px] px-5 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {busy ? "Saving…" : "Save — goes live instantly"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageEditor({ ed, current, onClose, save }) {
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(current);
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1600 / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        setUrl(dataUrl); setPreview(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const doSave = async (v) => {
    setBusy(true);
    if (await save(ed.ck, v)) onClose();
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-cyan w-full max-w-xl animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="content-image-editor">
        <div className="flex items-center justify-between p-4 border-b border-pb-border">
          <span className="font-display font-black uppercase text-lg flex items-center gap-2"><ImageIcon className="w-4 h-4 text-pb-cyan" /> Change Image</span>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="image-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {preview && <img src={preview} alt="" className="w-full h-40 object-cover border border-pb-border" data-testid="image-editor-preview" />}
          <div className="flex gap-2">
            <input data-testid="content-image-url" value={url.startsWith("data:") ? "" : url} onChange={(e) => { setUrl(e.target.value); if (e.target.value) setPreview(e.target.value); }}
              placeholder="Paste an image URL…"
              className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-white text-sm focus:outline-none focus:border-pb-cyan" />
            <label className="cursor-pointer border border-pb-border text-zinc-300 text-[11px] font-bold uppercase tracking-widest px-3 py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors flex items-center">
              Upload Photo
              <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="content-image-file" />
            </label>
          </div>
          <div className="flex gap-2 justify-between">
            <button data-testid="content-image-reset" disabled={busy} onClick={() => doSave(null)}
              className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[11px] font-bold uppercase tracking-widest px-3 py-2 hover:border-pb-red hover:text-pb-red transition-colors">
              <RotateCcw className="w-3 h-3" /> Reset to default
            </button>
            <button data-testid="content-image-save" disabled={busy || !url} onClick={() => doSave(url)}
              className="bg-pb-cyan text-black font-bold uppercase tracking-widest text-[11px] px-5 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {busy ? "Saving…" : "Save — goes live instantly"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ContentProvider({ children }) {
  const { realUser, preview, setPreview } = useAuth();
  const isAdmin = realUser?.role === "admin";
  const [content, setContent] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [textEd, setTextEd] = useState(null);
  const [imgEd, setImgEd] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [previewMenu, setPreviewMenu] = useState(false);

  // ---- Global text overrides: anchored to the exact component they were edited in ----
  const dynamicRef = { current: {} };
  for (const [k, v] of Object.entries(content)) {
    if (k.startsWith("txt.")) {
      try {
        const p = JSON.parse(v);
        // legacy overrides (no anchor) are ignored — they caused cross-page collisions
        if (p && p.o && typeof p.v === "string" && "a" in p)
          dynamicRef.current[`${p.a || ""}|${p.o}`] = { key: k, o: p.o, v: p.v, a: p.a || "" };
      } catch {}
    }
  }
  const dynamicJson = JSON.stringify(dynamicRef.current);

  useEffect(() => {
    const entries = JSON.parse(dynamicJson);
    let raf = null;
    const apply = () => {
      raf = null;
      document.querySelectorAll("[data-ck-orig]").forEach((el) => {
        const orig = el.dataset.ckOrig;
        const anc = el.dataset.ckAnchor || "";
        if (!entries[`${anc}|${orig}`]) {
          const node = Array.from(el.childNodes).find((c) => c.nodeType === 3 && c.nodeValue.trim().length > 0);
          if (node) node.nodeValue = orig;
          delete el.dataset.ckOrig;
          delete el.dataset.ckAnchor;
        }
      });
      const list = Object.values(entries);
      if (!list.length) return;
      const allAnchored = list.some((ov) => ov.a) ? Array.from(document.querySelectorAll("[data-testid]")) : [];
      for (const ov of list) {
        const roots = ov.a ? allAnchored.filter((e) => e.dataset.testid === ov.a) : [document.body];
        for (const root of roots) {
          const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
          let n;
          while ((n = walker.nextNode())) {
            const p = n.parentElement;
            if (!p || SKIP_TAGS.has(p.tagName) || inEditUi(p)) continue;
            const t = n.nodeValue?.trim();
            if (!t || t !== ov.o || t === ov.v) continue;
            // an unanchored edit must never touch elements owned by an anchored component edit
            if (!ov.a && p.closest("[data-ck-anchor]")) continue;
            n.nodeValue = n.nodeValue.replace(t, ov.v);
            p.dataset.ckOrig = t;
            p.dataset.ckAnchor = ov.a || "";
          }
        }
      }
    };
    const obs = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(apply); });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    apply();
    return () => { obs.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [dynamicJson]);

  useEffect(() => {
    if (!editMode) return;
    const click = (e) => {
      const el = e.target;
      if (inEditUi(el) || el.closest?.("[data-testid^='editable-']")) return;
      const node = directText(el);
      if (!node) return;
      e.preventDefault();
      e.stopPropagation();
      const current = node.nodeValue.trim();
      const orig = el.dataset.ckOrig || current;
      const anchor = el.closest("[data-testid]")?.dataset.testid || "";
      setTextEd({ ck: `txt.${hashText(`${anchor}|${orig}`)}`, fallback: orig, dynamicOrig: orig, anchor, currentVal: current });
    };
    const over = (e) => {
      const el = e.target;
      if (inEditUi(el) || SKIP_TAGS.has(el.tagName) || !directText(el)) return;
      el.style.outline = "1px dashed rgba(34,211,238,.75)";
      el.style.outlineOffset = "2px";
      el.style.cursor = "pointer";
    };
    const out = (e) => { e.target.style.outline = ""; e.target.style.outlineOffset = ""; e.target.style.cursor = ""; };
    document.addEventListener("click", click, true);
    document.addEventListener("mouseover", over, true);
    document.addEventListener("mouseout", out, true);
    return () => {
      document.removeEventListener("click", click, true);
      document.removeEventListener("mouseover", over, true);
      document.removeEventListener("mouseout", out, true);
    };
  }, [editMode]);

  useEffect(() => { api.get("/content").then(({ data }) => setContent(data)).catch(() => {}); }, []);
  useEffect(() => { if (!isAdmin) setEditMode(false); }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) api.get("/membership/tiers").then(({ data }) => setTiers(Array.isArray(data) ? data : data.tiers || [])).catch(() => {});
  }, [isAdmin]);

  const save = useCallback(async (key, value, opts = {}) => {
    try {
      await api.put("/content", { key, value });
      setContent((c) => {
        const n = { ...c };
        if (value) n[key] = value; else delete n[key];
        return n;
      });
      if (!opts.quiet) toast.success(value ? "Saved — live for everyone" : "Reset to default");
      return true;
    } catch { if (!opts.quiet) toast.error("Couldn't save content"); return false; }
  }, []);

  return (
    <Ctx.Provider value={{
      content, editMode, isAdmin,
      openText: (ck, fallback) => setTextEd({ ck, fallback }),
      openImage: (ck, fallback) => setImgEd({ ck, fallback }),
      save,
    }}>
      {children}
      {isAdmin && !preview && (
        <div className="fixed bottom-6 left-6 z-[90] flex items-end gap-2" data-edit-ui="true">
          <button data-testid="edit-mode-toggle" onClick={() => setEditMode((v) => !v)}
            title={editMode ? "Exit Edit Mode" : "Edit Mode — edit text & photos on this page"}
            className={`flex items-center gap-2 px-4 py-3 font-black uppercase tracking-widest text-[11px] shadow-xl transition-all active:scale-95 ${
              editMode ? "bg-pb-cyan text-black" : "bg-zinc-900 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black"}`}>
            <Pencil className="w-4 h-4" /> {editMode ? "Editing — click any outlined text" : "Edit Mode"}
          </button>
          <div className="relative">
            {previewMenu && (
              <div className="absolute bottom-full mb-2 left-0 bg-zinc-900 border border-pb-cyan/60 shadow-2xl min-w-[220px]" data-testid="preview-menu">
                <div className="px-3 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500 border-b border-pb-border">Preview site as…</div>
                {[
                  ...tiers.map((t) => ({ label: `${t.name || t.key} Member`, patch: { membership: t.key || t.id } })),
                  { label: "Coach", patch: { is_coach: true, membership: "free" } },
                  { label: "Tournament Director", patch: { is_td: true, membership: "free" } },
                ].map((p) => (
                  <button key={p.label} data-testid={`preview-as-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => { setPreviewMenu(false); setEditMode(false); setPreview(p); }}
                    className="w-full text-left px-3 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:bg-pb-cyan hover:text-black transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            )}
            <button data-testid="preview-toggle" onClick={() => setPreviewMenu((v) => !v)} title="Preview the site as a member, coach or TD"
              className="flex items-center gap-2 px-4 py-3 font-black uppercase tracking-widest text-[11px] shadow-xl bg-zinc-900 border border-[#A3E635]/60 text-[#A3E635] hover:bg-[#A3E635] hover:text-black transition-all active:scale-95">
              <Eye className="w-4 h-4" /> Preview As
            </button>
          </div>
        </div>
      )}
      {isAdmin && preview && (
        <button data-testid="exit-preview" onClick={() => setPreview(null)}
          className="fixed bottom-6 left-6 z-[95] flex items-center gap-2 px-4 py-3 font-black uppercase tracking-widest text-[11px] shadow-xl bg-[#A3E635] text-black hover:bg-pb-cyan transition-all active:scale-95 animate-pulse">
          <EyeOff className="w-4 h-4" /> Viewing as {preview.label} — Exit Preview
        </button>
      )}
      {textEd && <TextEditor ed={textEd}
        current={textEd.dynamicOrig ? (textEd.currentVal ?? textEd.fallback) : (content[textEd.ck] ?? textEd.fallback)}
        onClose={() => setTextEd(null)}
        save={(k, v) => save(k, v == null ? null : (textEd.dynamicOrig ? JSON.stringify({ o: textEd.dynamicOrig, v, a: textEd.anchor || "" }) : v))} />}
      {imgEd && <ImageEditor ed={imgEd} current={content[imgEd.ck] || imgEd.fallback} onClose={() => setImgEd(null)} save={save} />}
    </Ctx.Provider>
  );
}
